import { ref, Ref } from 'vue'
import { WebSocketEvent } from '@/types/constantsEnum'
import { getToken } from '@/utils/auth'
import { checkVersion } from '@/utils/versionCheck'

// ==================== 类型定义 ====================
export interface WsMessage {
  event: WebSocketEvent
  data: any
}

export interface WsResponse extends WsMessage {
  success: boolean
  message?: string
}

type EventHandler<T = any> = (data: T) => void

interface WebSocketOptions {
  url: string
  reconnectLimit?: number
  reconnectInterval?: number
  heartBeatInterval?: number
}

// 版本本地存储键名
const VERSION_CHECK_KEY = 'app_version_check'
// 获取当前应用版本
const CURRENT_VERSION = '1.2.2'

// ==================== WebSocket 客户端类 ====================
export class WebSocketClient {
  private ws: WebSocket | null = null
  private reconnectCount = 0
  private eventListeners = new Map<string, EventHandler[]>()
  private options: Required<WebSocketOptions>
  private heartBeatTimer: number | null = null
  private reconnectTimer: number | null = null

  public isConnected: Ref<boolean> = ref(false)
  public messages: Ref<WsResponse[]> = ref([])

  constructor(options: WebSocketOptions) {
    this.options = {
      reconnectLimit: 10,
      url: options.url,
      reconnectInterval: 5000,
      heartBeatInterval: 5000,
      ...options,
    }
    this.connect()
  }

  // ==================== 核心方法 ====================
  private connect() {
    this.cleanup()
    this.ws = new WebSocket(this.options.url)

    this.ws.onopen = () => this.handleOpen(false)
    this.ws.onmessage = (event) => this.handleMessage(event)
    this.ws.onerror = () => this.handleError()
    this.ws.onclose = () => this.handleClose()
  }

  private handleOpen(isReconnect: boolean = false) {
    this.isConnected.value = true
    if (!isReconnect) {
      this.reconnectCount = 0
    }
    this.startHeartBeat()
    this.emit('open')
  }

  private handleMessage(event: MessageEvent) {
    try {
      const message = JSON.parse(event.data) as WsResponse
      this.messages.value.push(message)

      // 检查是否为PONG消息，如果是，检查版本
      if (message.event === WebSocketEvent.Pong && message.data && message.data.frontEndVersion) {
        checkVersion(message.data.frontEndVersion)
      }

      this.emit(message.event, message)
      this.emit('message', message)
    } catch (error) {
      console.error('解析消息失败:', error)
    }
  }

  private handleError() {
    console.log('连接失败---')
    this.emit('error')
    this.reconnect()
  }

  private handleClose() {
    this.isConnected.value = false
    console.log('连接关闭---')
    this.cleanup()
    this.emit('close')
    if (this.reconnectCount < this.options.reconnectLimit) {
      this.reconnect()
    } else {
      console.log('重连结束')
    }
  }

  // ==================== 消息发送 ====================
  public send(message: WsMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message))
      } catch (error) {
        console.error('发送消息失败:', error)
      }
    }
  }

  // ==================== 连接管理 ====================
  private reconnect() {
    this.reconnectCount++

    this.reconnectTimer = window.setTimeout(() => {
      // this.connect()
      this.reconnectSend()
    }, this.options.reconnectInterval)
  }

  private startHeartBeat() {
    this.heartBeatTimer = window.setInterval(() => {
      this.send({
        event: WebSocketEvent.Ping,
        data: {},
      })
    }, this.options.heartBeatInterval)
  }

  // 重连发送
  public reconnectSend() {
    this.cleanup()
    this.ws = new WebSocket(this.options.url)

    this.ws.onopen = () => {
      // 连接成功后发送认证消息
      const tokenData = getToken()
      if (tokenData && tokenData.token) {
        this.send({
          event: WebSocketEvent.UserEvent,
          data: { Authorization: tokenData.token },
        })
      } else {
        this.send({
          event: WebSocketEvent.UserEvent,
          data: {},
        })
      }

      this.handleOpen(true)
    }

    this.ws.onmessage = (event) => this.handleMessage(event)
    this.ws.onerror = () => this.handleError()
    this.ws.onclose = () => this.handleClose()
  }
  public close() {
    this.cleanup()
    this.ws?.close()
  }

  private cleanup() {
    if (this.heartBeatTimer) clearInterval(this.heartBeatTimer)
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.heartBeatTimer = null
    this.reconnectTimer = null
  }

  // ==================== 事件管理 ====================
  public on<T>(event: string, handler: EventHandler<T>) {
    const handlers = this.eventListeners.get(event) || []
    handlers.push(handler)
    this.eventListeners.set(event, handlers)
  }

  public off<T>(event: string, handler: EventHandler<T>) {
    const handlers = this.eventListeners.get(event) || []
    const index = handlers.indexOf(handler)
    if (index > -1) handlers.splice(index, 1)
    this.eventListeners.set(event, handlers)
  }

  private emit<T>(event: string, data?: T) {
    const handlers = this.eventListeners.get(event) || []
    handlers.forEach((handler) => handler(data))
  }
}

// ==================== 组合式函数封装 ====================
const { VITE_APP_WS_URL } = import.meta.env

export function useWebSocket() {
  const wsClient = new WebSocketClient({ url: VITE_APP_WS_URL })

  const on = <K>(event: string, handler: EventHandler<K>) => {
    wsClient.on(event, handler)
    return () => wsClient.off(event, handler)
  }

  const onMessageType = (type: WebSocketEvent, handler: EventHandler<WsResponse>) => {
    return on(type, handler)
  }

  const send = (message: WsMessage) => {
    wsClient.send(message)
  }

  return {
    isConnected: wsClient.isConnected,
    messages: wsClient.messages,
    send,
    on,
    onMessageType,
    close: () => wsClient.close(),
  }
}
